import type {
  AvatarList,
  FollowMeProfile,
  FollowMeSharedBet,
  FollowMeStats,
} from '@core/Areas/FollowMe/types'
import {
  CopiedFeedPageSize,
  FollowFeedPageSize,
  ShareFeedPageSize,
} from '@core/Areas/FollowMe/constants'
import type {
  CreateProfileResponse,
  FollowMeFeedParams,
  PostMultipleSharesToFollowMeResponse,
  UpdateProfile,
} from './types'
import { postToBetLoopApi, getFromBetLoopApi, putToBetLoopApi } from './helpers/apiServiceWrapper'

const enum LocalConstants {
  FollowMeV1ApiBase = '/api/followme/v1',
}

export const postShareToFollowMe = ({ ticketSequence }: { ticketSequence: number }) =>
  postToBetLoopApi<{ postId: number }>({
    url: `${LocalConstants.FollowMeV1ApiBase}/share/bet`,
    body: { ticketSequence },
  })

export const postMultipleSharesToFollowMe = ({ ticketSequences }: { ticketSequences: number[] }) =>
  postToBetLoopApi<PostMultipleSharesToFollowMeResponse>({
    url: `${LocalConstants.FollowMeV1ApiBase}/share/bets`,
    body: { ticketSequences },
  })

export const postCreateFollowMeProfile = () =>
  postToBetLoopApi<CreateProfileResponse>({
    url: `${LocalConstants.FollowMeV1ApiBase}/account/`,
    body: {},
  })

export const getFollowMeSharedBetsFeed = async ({
  pageParam = null,
  sortOrder = 'sortAsc',
  sortFilter = 'JumpTime',
  feedType = 'explore',
  accountId = null,
  fromPostId = undefined,
  pageSize = ShareFeedPageSize,
}: FollowMeFeedParams) => {
  const params = new URLSearchParams()
  params.append('pageSize', (pageSize ?? ShareFeedPageSize).toString())

  const isFollowing = feedType === 'following'

  params.append('following', `${isFollowing}`)

  if (accountId !== null && (feedType === 'pending' || feedType === 'resulted')) {
    params.append('account', accountId)
  }

  if (sortOrder === 'sortAsc') {
    params.append('sortAsc', sortFilter)
  } else {
    params.append('sortDesc', sortFilter)
  }

  if (fromPostId) {
    params.append('from', fromPostId.toString())
    params.append('newPosts', 'true')
  } else if (pageParam !== null) {
    params.append('from', pageParam.toString())
  }

  const url =
    feedType !== 'resulted'
      ? `${LocalConstants.FollowMeV1ApiBase}/share/feed?${params.toString()}`
      : `${LocalConstants.FollowMeV1ApiBase}/share/resulted?${params.toString()}`
  return getFromBetLoopApi<FollowMeSharedBet[]>({ url })
}

export const getCopiedBetsFeed = async ({ pageParam = null }: { pageParam?: number | null }) => {
  const params = new URLSearchParams()
  params.append('pageSize', CopiedFeedPageSize.toString())
  if (pageParam !== null) {
    params.append('from', pageParam.toString())
  }

  const url = `${LocalConstants.FollowMeV1ApiBase}/share/copied?${params.toString()}`
  return getFromBetLoopApi<FollowMeSharedBet[]>({ url })
}

export const getPrincipleFollowMeUserProfile = () => {
  return getFromBetLoopApi<FollowMeProfile>({ url: `${LocalConstants.FollowMeV1ApiBase}/account` })
}

export const getPrincipleFollowMeUserProfileStats = (accountId: string) => {
  return getFromBetLoopApi<FollowMeStats>({
    url: `${LocalConstants.FollowMeV1ApiBase}/account/${accountId}/stats`,
  })
}

export const getFollowMeProfileFollowAccountsPaged = async ({
  pageParam = null,
  accountId = undefined,
  isFollowing = true,
}: {
  pageParam: string | null
  accountId: string | undefined
  isFollowing?: boolean
}) => {
  const params = new URLSearchParams()
  if (pageParam) {
    params.append('from', pageParam.toString())
  }
  if (accountId) {
    params.append('accountId', accountId)
  }
  params.append('pageSize', FollowFeedPageSize.toString())
  const accountFollowUri = isFollowing ? 'following' : 'followed'
  const url = `${LocalConstants.FollowMeV1ApiBase}/account/${accountFollowUri}?${params.toString()}`
  return getFromBetLoopApi<FollowMeProfile[]>({ url })
}

export function postFollowMeFollowAccount(accountId: string) {
  return postToBetLoopApi<void>({
    url: `${LocalConstants.FollowMeV1ApiBase}/account/follow`,
    body: { followsAccountId: accountId },
  })
}

export function postFollowMeUnfollowAccount(accountId: string) {
  return postToBetLoopApi<void>({
    url: `${LocalConstants.FollowMeV1ApiBase}/account/unfollow`,
    body: { accountToUnfollowId: accountId },
  })
}

export function postFollowMeReportProfile(accountId: string) {
  return postToBetLoopApi<void>({
    url: `${LocalConstants.FollowMeV1ApiBase}/account/report`,
    body: { accountId },
  })
}

export function postFollowedAccountNotifications(accountId: string, shouldNotify: boolean) {
  return postToBetLoopApi<void>({
    url: `${LocalConstants.FollowMeV1ApiBase}/account/notification/set`,
    body: { followedAccountId: accountId, notify: shouldNotify },
  })
}

export const getFollowMeUserProfileById = (id: string) =>
  getFromBetLoopApi<FollowMeProfile>(
    { url: `${LocalConstants.FollowMeV1ApiBase}/account/${id}` },
    { skipGlobalBanCheck: true }
  )

export const getFollowMeAvatarList = async () => {
  return await getFromBetLoopApi<AvatarList[]>({
    url: `${LocalConstants.FollowMeV1ApiBase}/account/avatars`,
  })
}

export const putFollowMeProfileDetails = (body: UpdateProfile) => {
  return putToBetLoopApi<UpdateProfile>({
    url: `${LocalConstants.FollowMeV1ApiBase}/account`,
    body,
  })
}

export const getFollowMeSharedBetByPostId = (postId: number) =>
  getFromBetLoopApi<FollowMeSharedBet>({
    url: `${LocalConstants.FollowMeV1ApiBase}/share/post/${postId}`,
  })

export const getFollowMeCanShareTickets = (ticketSequences: string[]) =>
  postToBetLoopApi<{ canShare: boolean; ticketSequence: number }[]>({
    url: `${LocalConstants.FollowMeV1ApiBase}/share/tickets/can-share`,
    body: { ticketSequences },
  })
